import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DynamicContentRenderer - Fetches and renders content sections for a given page
 * @param {string} pageKey - The unique key for the page (e.g., 'admin-dashboard')
 * @param {React.ReactNode} children - Fallback content if no sections exist or loading
 */
export const DynamicContentRenderer = ({ pageKey, children }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/sections`, {
          params: { page_key: pageKey }
        });
        
        // Filter and sort sections
        const pageSections = response.data
          .filter(s => s.page_key === pageKey && s.is_enabled)
          .sort((a, b) => a.order - b.order);
        
        setSections(pageSections);
      } catch (err) {
        console.error('Error fetching content sections:', err);
        setError('Failed to load dynamic content');
      } finally {
        setLoading(false);
      }
    };

    if (pageKey) {
      fetchSections();
    }
  }, [pageKey]);

  // If loading or no sections, show children (original content)
  if (loading || sections.length === 0) {
    return <>{children}</>;
  }

  // If error, show error message but still render children
  if (error) {
    return (
      <>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        {children}
      </>
    );
  }

  // Render dynamic sections
  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <SectionRenderer key={section.id} section={section} />
      ))}
      
      {/* Still render original children after dynamic sections */}
      {children}
    </div>
  );
};

/**
 * SectionRenderer - Renders a single section based on its type
 */
const SectionRenderer = ({ section }) => {
  const { section_type, content } = section;

  switch (section_type) {
    case 'hero':
      return <HeroSection section={section} content={content} />;
    
    case 'stats':
      return <StatsSection section={section} content={content} />;
    
    case 'features':
      return <FeaturesSection section={section} content={content} />;
    
    case 'cta':
      return <CTASection section={section} content={content} />;
    
    case 'content':
      return <ContentSection section={section} content={content} />;
    
    default:
      return <DefaultSection section={section} content={content} />;
  }
};

// Section Type Components

const HeroSection = ({ section, content }) => (
  <div className="relative rounded-lg bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-white shadow-xl">
    {content?.badge_text && (
      <Badge className="mb-4 bg-yellow-500 text-black hover:bg-yellow-400">
        {content.badge_text}
      </Badge>
    )}
    <h1 className="text-4xl font-bold mb-4">{content?.heading || section.title}</h1>
    {content?.subheading && (
      <p className="text-xl mb-6 text-blue-100">{content.subheading}</p>
    )}
    {content?.body_text && (
      <p className="text-blue-50 mb-6 max-w-2xl">{content.body_text}</p>
    )}
    {content?.cta_text && (
      <Button 
        variant="secondary" 
        size="lg"
        onClick={() => {
          if (content?.cta_link) {
            window.location.href = content.cta_link;
          }
        }}
      >
        {content.cta_text}
      </Button>
    )}
  </div>
);

const StatsSection = ({ section, content }) => {
  const items = content?.items || [];
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{content?.heading || section.title}</CardTitle>
        {content?.subheading && <CardDescription>{content.subheading}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map((item, idx) => (
            <div key={idx} className="text-center p-4 border rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{item.value}</div>
              <div className="text-sm text-muted-foreground mt-1">{item.label}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const FeaturesSection = ({ section, content }) => {
  const items = content?.items || [];
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{content?.heading || section.title}</CardTitle>
        {content?.subheading && <CardDescription>{content.subheading}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, idx) => (
            <div key={idx} className="p-4 border rounded-lg hover:border-blue-500 transition-colors">
              {item.icon && (
                <div className="text-blue-600 mb-2 text-lg font-bold">{item.icon}</div>
              )}
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.description}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const CTASection = ({ section, content }) => (
  <Card className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-2 border-blue-500">
    <CardContent className="p-8 text-center">
      <h2 className="text-2xl font-bold mb-2">{content?.heading || section.title}</h2>
      {content?.subheading && (
        <p className="text-muted-foreground mb-6">{content.subheading}</p>
      )}
      {content?.body_text && (
        <p className="mb-6">{content.body_text}</p>
      )}
      {content?.cta_text && (
        <Button 
          size="lg"
          onClick={() => {
            if (content?.cta_link) {
              window.location.href = content.cta_link;
            }
          }}
        >
          {content.cta_text}
        </Button>
      )}
    </CardContent>
  </Card>
);

const ContentSection = ({ section, content }) => (
  <Card>
    <CardHeader>
      {content?.heading && <CardTitle>{content.heading}</CardTitle>}
      {content?.subheading && <CardDescription>{content.subheading}</CardDescription>}
    </CardHeader>
    <CardContent>
      {content?.image_url && (
        <img 
          src={content.image_url} 
          alt={content.heading || 'Content'} 
          className="w-full rounded-lg mb-4"
        />
      )}
      {content?.body_text && (
        <div className="prose dark:prose-invert max-w-none">
          <p>{content.body_text}</p>
        </div>
      )}
    </CardContent>
  </Card>
);

const DefaultSection = ({ section, content }) => (
  <Card>
    <CardHeader>
      <CardTitle>{section.title}</CardTitle>
      <Badge>{section.section_type}</Badge>
    </CardHeader>
    <CardContent>
      <pre className="text-xs bg-muted p-4 rounded overflow-auto">
        {JSON.stringify(content, null, 2)}
      </pre>
    </CardContent>
  </Card>
);

export default DynamicContentRenderer;
